'use client'

import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { Label, PolarGrid, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Check,
  ClipboardList,
  GripVertical,
  Pencil,
  PieChart,
  Plus,
  Shield,
  Target,
  Trash2,
  TrendingUp,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { ChartContainer, type ChartConfig } from '@/components/ui/chart'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorBoundary } from '@/components/errors/error-boundary'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDate } from '@/lib/format/date'
import { ScoreTierBar } from '@/features/cases/components/score-tier-bar'
import { nextWealthScoreBand, wealthScoreBand } from '@/features/cases/lib/wealth-score'
import {
  createCasePrescriptionStepSchema,
  editCasePrescriptionStepSchema,
  type CreateCasePrescriptionStepFormValues,
  type EditCasePrescriptionStepFormValues,
} from '@/features/cases/schemas/case-forms.schema'
import type {
  CaseRecord,
  CreateCasePrescriptionStepInput,
  PrescriptionStep,
  PrescriptionStepPriority,
} from '@/features/cases/types'

const scoreGaugeConfig = { value: { label: 'Score' } } satisfies ChartConfig

/** A step's icon rotates through a small, content-agnostic set — steps are freeform advisor text,
 * not tagged with a category, so this is decorative variety rather than a meaningful per-step icon. */
const STEP_ICONS = [PieChart, Shield, Target, TrendingUp]

/**
 * "You are here, this is the next tier, this is how you get there" — a level-up bar rather than a
 * forecast number, since we can promise a path, not a specific future score. Shown only on the
 * read-only view: it's meant to frame *these steps*, which only exist once an advisor has authored
 * them, as the thing that moves the needle — not a standalone prediction.
 */
export function ScoreProgress({ score, stepsToGo }: { score: number; stepsToGo: number }) {
  const band = wealthScoreBand(score)
  const next = nextWealthScoreBand(score)
  // Floor of 2 so a zero score still shows a sliver of the first band rather than an empty bar.
  const pct = Math.max(2, Math.min(100, score))

  const scoreGaugeData = [{ metric: 'score', value: score, fill: band.color }]

  return (
    <div className="bg-muted/40 flex flex-col gap-4 rounded-lg border p-4">
      <div className="@lg:flex-row @lg:items-center flex flex-col gap-4">
        <div className="@lg:border-r @lg:pr-4 flex shrink-0 items-center gap-4">
          <ChartContainer config={scoreGaugeConfig} className="aspect-square h-24 w-24 shrink-0">
            <RadialBarChart
              data={scoreGaugeData}
              startAngle={90}
              endAngle={90 - (score / 100) * 360}
              innerRadius={38}
              outerRadius={48}
            >
              <PolarGrid
                gridType="circle"
                radialLines={false}
                stroke="none"
                className="first:fill-muted last:fill-background"
                polarRadius={[41, 35]}
              />
              <RadialBar dataKey="value" background cornerRadius={6} />
              <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) - 4}
                            className="fill-foreground text-xl font-bold tabular-nums"
                          >
                            {score}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 12}
                            className="fill-muted-foreground text-[9px]"
                          >
                            out of 100
                          </tspan>
                        </text>
                      )
                    }
                  }}
                />
              </PolarRadiusAxis>
            </RadialBarChart>
          </ChartContainer>
          <div className="flex min-w-0 flex-col gap-1.5">
            <span
              className="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{
                backgroundColor: `color-mix(in oklch, ${band.color} 16%, transparent)`,
                color: band.color,
              }}
            >
              <span className="size-1.5 rounded-full" style={{ backgroundColor: band.color }} />{' '}
              {band.label}
            </span>
            <p className="text-muted-foreground text-xs leading-snug">
              {!next ? "You're in our top band." : "You're on the right track."}
              <br />
              {!next
                ? 'Keep following your prescription to stay there.'
                : `A few steps away from ${next.label}.`}
            </p>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {next && (
            <div className="flex justify-end">
              <span className="text-primary text-xs font-medium">
                {next.pointsAway} pts to {next.label}
              </span>
            </div>
          )}
          <ScoreTierBar score={pct} showScale />
          <p className="text-primary text-xs leading-relaxed">
            {!next
              ? "You're in our top band — the steps below are about staying there as markets move."
              : stepsToGo > 0
                ? `Completing the ${stepsToGo === 1 ? 'step' : `${stepsToGo} steps`} below is how investors in the ${band.label} band typically move up to ${next.label}.`
                : `Keep an eye out here — your advisor's next steps are what typically move investors from ${band.label} to ${next.label}.`}
          </p>
        </div>
      </div>
    </div>
  )
}

/** Same mark used in the sidebar/header — a small brand anchor on the one card explicitly framed as
 * "Nivex's" advice, not just a generic list. */
function NivexMark() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect x="6" y="7" width="7" height="28" rx="1.5" fill="currentColor" />
      <polygon points="13,7 21,7 35,35 27,35" fill="currentColor" />
      <rect x="35" y="13" width="7" height="28" rx="1.5" fill="currentColor" />
    </svg>
  )
}

/**
 * Wraps one step `<li>` with dnd-kit's sortable wiring. Always mounted (even when reordering is
 * off, e.g. the read-only investor view or a `limit`-ed summary) so `useSortable` is called
 * unconditionally on every render — `disabled` is what actually turns dragging on/off, rather than
 * conditionally mounting this wrapper only for the editable case.
 */
function SortableStepCard({
  id,
  disabled,
  children,
}: {
  id: string
  disabled: boolean
  children: (drag: {
    attributes: ReturnType<typeof useSortable>['attributes']
    listeners: ReturnType<typeof useSortable>['listeners']
  }) => ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  })
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`bg-card shadow-xs flex flex-col gap-3 rounded-xl border p-4 ${isDragging ? 'z-10 opacity-50' : ''}`}
    >
      {children({ attributes, listeners })}
    </li>
  )
}

/** The card's own header — same icon-badge + title/description shape as every other named section on
 * this dashboard, so it doesn't read as a stray list bolted on at the end. */
function PrescriptionHeader() {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-start gap-2.5">
        <span className="bg-primary/10 text-primary mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
          <NivexMark />
        </span>
        <div>
          <p className="text-sm font-semibold">Nivex Suggestions</p>
          <p className="text-muted-foreground text-xs">
            Steps your advisor recommends to grow this portfolio.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * The advisor's actual prescription for a case — short, ordered, written by
 * them. Shared by staff/admin (who author it) and the investor dashboard (who
 * only reads it), so the two can never show different steps. `canEdit` is the
 * only thing that differs.
 */
export function CasePrescription(props: {
  caseRecord: CaseRecord
  canEdit?: boolean
  limit?: number
  /** Real data, fed in by every caller (staff/admin `client-detail.tsx` and the real investor
   * dashboard) — this component no longer fetches steps itself. */
  injectedSteps?: PrescriptionStep[]
  /** Injected real mutations — when provided, add/edit/remove/reorder/toggle call the real
   * backend instead of the (mostly unreachable) mock-mutation fallback. Edit/reorder/toggle are
   * staff-only additions — the read-only investor view never gets edit/reorder. */
  onAddStep?: (input: CreateCasePrescriptionStepInput) => Promise<void>
  onEditStep?: (id: string, input: CreateCasePrescriptionStepInput) => Promise<void>
  onRemoveStep?: (id: string) => void
  onReorderStep?: (id: string, position: number) => void
  onToggleStepDone?: (id: string, done: boolean) => void
}) {
  return (
    <ErrorBoundary label="this prescription" resetKeys={[props.caseRecord.id]}>
      <PrescriptionBody {...props} />
    </ErrorBoundary>
  )
}

function PrescriptionBody({
  caseRecord,
  canEdit = false,
  limit,
  injectedSteps,
  onAddStep,
  onEditStep,
  onRemoveStep,
  onReorderStep,
  onToggleStepDone,
}: {
  caseRecord: CaseRecord
  canEdit?: boolean
  limit?: number
  injectedSteps?: PrescriptionStep[]
  onAddStep?: (input: CreateCasePrescriptionStepInput) => Promise<void>
  onEditStep?: (id: string, input: CreateCasePrescriptionStepInput) => Promise<void>
  onRemoveStep?: (id: string) => void
  onReorderStep?: (id: string, position: number) => void
  onToggleStepDone?: (id: string, done: boolean) => void
}) {
  const steps = injectedSteps ?? []

  // `onRemoveStep` is a staff-only real mutation, always supplied by `client-detail.tsx` when
  // `canEdit` is true. `onToggleStepDone` is supplied by BOTH the staff-editable branch
  // (`client-detail.tsx`, hitting the general staff PATCH endpoint, gated below behind a confirm
  // dialog since staff acts on the investor's behalf) and the read-only investor branch
  // (`investor-audit-prescription-view.tsx`, hitting the separate investor-only `.../done`
  // endpoint, immediate — it's the investor's own action on their own step). Admin never supplies
  // it, so the read-only branch's button below is gated on its presence rather than rendering
  // unconditionally — a click that admin's RBAC would otherwise 403 on never becomes clickable.
  const removeStep = (id: string) => onRemoveStep?.(id)

  // Staff's own toggle is confirm-gated — it reflects on the investor's dashboard and
  // awards/reverses wealth-score points, so it isn't fired straight from the click handler.
  const [confirmingToggle, setConfirmingToggle] = useState<{
    step: PrescriptionStep
    next: boolean
  } | null>(null)
  const confirmStaffToggle = () => {
    if (confirmingToggle) onToggleStepDone?.(confirmingToggle.step.id, confirmingToggle.next)
    setConfirmingToggle(null)
  }

  // Any role can open a step to read its full title/description — the card itself only ever
  // shows a 2-line clamp of the description, which isn't enough for a long, real prescription.
  const [viewingStep, setViewingStep] = useState<PrescriptionStep | null>(null)

  // Staff-only — opens the edit dialog for one step. Prefilled from the step being edited.
  const [editingStep, setEditingStep] = useState<PrescriptionStep | null>(null)

  // Real, structured form — mirrors `CreatePrescriptionStepDto` exactly. Only rendered when
  // `onAddStep` is supplied (the real, staff-editable path).
  const structuredForm = useForm<
    z.input<typeof createCasePrescriptionStepSchema>,
    unknown,
    CreateCasePrescriptionStepFormValues
  >({
    resolver: zodResolver(createCasePrescriptionStepSchema),
    mode: 'onTouched',
    defaultValues: { title: '', description: '', priority: 'medium', impactPoints: 5 },
  })
  const onStructuredSubmit = structuredForm.handleSubmit(async (values) => {
    if (!onAddStep) return
    await onAddStep(values)
    structuredForm.reset()
  })

  const shown = limit ? steps.slice(0, limit) : steps

  // Reorder controls (both drag and the arrow-button fallback) only make sense against the full,
  // unsliced list — `limit` is only ever used by the read-only overview summary, which never sets
  // `canEdit`, so this guard never actually fires today; it's here so a future limited+editable
  // view can't move a step across a boundary it can't see.
  const canReorderList = canEdit && !!onReorderStep && !limit

  // PointerSensor (mouse/touch drag) + KeyboardSensor (space/enter to pick up, arrow keys to move,
  // space/enter to drop) — dnd-kit's own accessible keyboard support, on top of which the
  // up/down icon buttons stay as a second, always-visible way to reorder.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    if (!canReorderList) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = shown.findIndex((s) => s.id === active.id)
    const newIndex = shown.findIndex((s) => s.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    onReorderStep!(String(active.id), newIndex + 1)
  }

  const addForm = canEdit && onAddStep && (
    <form onSubmit={onStructuredSubmit} className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="@md/main:grid-cols-2 grid grid-cols-1 gap-3">
        <Field>
          <FieldLabel htmlFor="step-title">Title</FieldLabel>
          <Input
            id="step-title"
            placeholder="e.g. Trim concentration risk"
            {...structuredForm.register('title')}
          />
          <FieldError errors={[structuredForm.formState.errors.title]} />
        </Field>
        <Field>
          <FieldLabel htmlFor="step-priority">Priority</FieldLabel>
          <Select
            value={structuredForm.watch('priority')}
            onValueChange={(v) =>
              structuredForm.setValue('priority', v as PrescriptionStepPriority)
            }
          >
            <SelectTrigger id="step-priority" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor="step-description">What should the client do?</FieldLabel>
        <Textarea
          id="step-description"
          rows={2}
          className="max-h-48 overflow-y-auto"
          placeholder="e.g. Trim your largest holding to under 30% of the portfolio."
          {...structuredForm.register('description')}
        />
        <FieldError errors={[structuredForm.formState.errors.description]} />
      </Field>
      <Field>
        <FieldLabel htmlFor="step-impact">Impact points (0-100)</FieldLabel>
        <Input
          id="step-impact"
          type="number"
          min={0}
          max={100}
          className="w-32"
          {...structuredForm.register('impactPoints')}
        />
        <FieldError errors={[structuredForm.formState.errors.impactPoints]} />
      </Field>
      <Button
        type="submit"
        size="sm"
        variant="outline"
        className="w-fit"
        disabled={structuredForm.formState.isSubmitting}
      >
        <Plus className="size-3.5" /> Add step
      </Button>
    </form>
  )

  if (steps.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <PrescriptionHeader />
        {!canEdit && <ScoreProgress score={caseRecord.wealthScore} stepsToGo={0} />}
        <Empty className="border border-dashed py-8">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ClipboardList />
            </EmptyMedia>
            <EmptyTitle>No steps yet</EmptyTitle>
            <EmptyDescription>
              {canEdit
                ? 'What should this client actually do next? Write it yourself.'
                : 'Your advisor will add specific next steps here once the review is complete.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
        {addForm}
      </div>
    )
  }

  const doneCount = steps.filter((s) => s.done).length
  const donePct = steps.length ? (doneCount / steps.length) * 100 : 0

  return (
    <div className="flex flex-col gap-3">
      <PrescriptionHeader />

      {!canEdit && (
        <ScoreProgress score={caseRecord.wealthScore} stepsToGo={steps.length - doneCount} />
      )}

      {!canEdit && (
        <div className="flex items-center gap-3 rounded-lg border px-3.5 py-3">
          <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-full">
            <ClipboardList className="size-4" />
          </span>
          <span className="shrink-0 text-sm">
            {doneCount} of {steps.length} steps completed
          </span>
          <div className="bg-muted h-1.5 min-w-0 flex-1 overflow-hidden rounded-full">
            <div
              className="bg-primary h-full rounded-full transition-[width]"
              style={{ width: `${donePct}%` }}
            />
          </div>
          <span className="shrink-0 text-sm font-semibold tabular-nums">
            {Math.round(donePct)}%
          </span>
        </div>
      )}

      {/* One card per step, two-up: the ordinal and date on a muted top line, the short title with
          its priority beside it, then a dashed rule over the footer holding the advisor's point
          estimate and the action. Drag-and-drop (mouse/touch/keyboard, via dnd-kit) and the
          up/down arrow buttons are both wired to the same `onReorderStep` mutation — two ways to
          trigger one existing action, not two separate reorder paths. */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={shown.map((s) => s.id)} strategy={rectSortingStrategy}>
          <ol className="@xl/main:grid-cols-2 @4xl/main:grid-cols-3 grid grid-cols-1 gap-3">
            {shown.map((step, i) => {
              const done = !!step.done
              const StepIcon = STEP_ICONS[i % STEP_ICONS.length] ?? PieChart
              return (
                <SortableStepCard key={step.id} id={step.id} disabled={!canReorderList}>
                  {({ attributes, listeners }) => (
                    <>
                      <button
                        type="button"
                        onClick={() => setViewingStep(step)}
                        className="hover:bg-muted/40 -m-1 flex flex-col gap-3 rounded-lg p-1 text-left transition-colors"
                      >
                        <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
                          <span className="truncate">Step {i + 1}</span>
                          <span className="shrink-0 tabular-nums">
                            {formatDate(step.createdAt)}
                          </span>
                        </div>

                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-1 items-start gap-2.5">
                            <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
                              <StepIcon className="size-4" />
                            </span>
                            <div className="min-w-0">
                              <p
                                className={`text-sm font-semibold ${done ? 'text-muted-foreground line-through' : ''}`}
                              >
                                {step.title ?? step.text}
                              </p>
                              {/* The full instruction still shows when a short title is standing in for it. */}
                              {step.title && (
                                <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
                                  {step.text}
                                </p>
                              )}
                            </div>
                          </div>
                          {step.priority && (
                            <span className="text-muted-foreground shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize">
                              {step.priority}
                            </span>
                          )}
                        </div>
                      </button>

                      <div className="border-t border-dashed" />

                      {/* One row: points on the left, every control (drag handle, edit,
                          mark-done/undo, remove) grouped on the right — compact icon buttons
                          instead of the old two-row, full-label layout. */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-primary text-sm font-semibold tabular-nums">
                          {step.impactPoints ? `+${step.impactPoints} pts` : ''}
                        </span>
                        {canEdit ? (
                          <div className="flex shrink-0 items-center gap-0.5">
                            {canReorderList && (
                              <button
                                type="button"
                                {...attributes}
                                {...listeners}
                                aria-label={`Drag to reorder ${step.title ?? 'step'}`}
                                className="text-muted-foreground hover:bg-muted flex size-7 shrink-0 cursor-grab touch-none items-center justify-center rounded-md active:cursor-grabbing"
                              >
                                <GripVertical className="size-3.5" />
                              </button>
                            )}
                            {onEditStep && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="text-muted-foreground size-7"
                                    aria-label="Edit step"
                                    onClick={() => setEditingStep(step)}
                                  >
                                    <Pencil className="size-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                            )}
                            {step.doneByInvestor ? (
                              // The investor has final say once they've marked this done
                              // themselves — no button here at all until they undo it (a plain
                              // status label, not a disabled control, since it isn't staff's to
                              // act on right now).
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-muted-foreground flex shrink-0 items-center gap-1 px-1.5 text-xs">
                                    <Check className="size-3" /> Done
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Marked done by the investor — only they can undo it
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant={done ? 'outline' : 'ghost'}
                                className="text-muted-foreground h-7 shrink-0 px-2 text-xs"
                                onClick={() => setConfirmingToggle({ step, next: !done })}
                                aria-pressed={done}
                                disabled={!onToggleStepDone}
                              >
                                {done ? 'Undo' : 'Mark done'}
                              </Button>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10 size-7"
                                  aria-label="Remove step"
                                  onClick={() => removeStep(step.id)}
                                >
                                  <Trash2 className="size-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Remove</TooltipContent>
                            </Tooltip>
                          </div>
                        ) : (
                          canReorderList && (
                            <button
                              type="button"
                              {...attributes}
                              {...listeners}
                              aria-label={`Drag to reorder ${step.title ?? 'step'}`}
                              className="text-muted-foreground hover:bg-muted flex size-7 shrink-0 cursor-grab touch-none items-center justify-center rounded-md active:cursor-grabbing"
                            >
                              <GripVertical className="size-3.5" />
                            </button>
                          )
                        )}
                      </div>

                      {!canEdit && onToggleStepDone && (
                        // Labelled by what it does. There is no per-step detail view to open, so
                        // calling this "View Detail" would be a button that goes nowhere.
                        // Immediate — this is the investor's own action on their own step, no
                        // confirmation needed.
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            size="sm"
                            variant={done ? 'outline' : 'default'}
                            className="shrink-0"
                            onClick={() => onToggleStepDone(step.id, !done)}
                            aria-pressed={done}
                          >
                            {done ? (
                              <>
                                <Check className="size-3.5" /> Done
                              </>
                            ) : (
                              'Mark done'
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </SortableStepCard>
              )
            })}
          </ol>
        </SortableContext>
      </DndContext>
      {limit && steps.length > limit && (
        <p className="text-muted-foreground text-xs">
          +{steps.length - limit} more on the Audit &amp; Prescription tab.
        </p>
      )}
      {addForm}
      <AlertDialog
        open={!!confirmingToggle}
        onOpenChange={(open) => !open && setConfirmingToggle(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Mark &ldquo;{confirmingToggle?.step.title ?? confirmingToggle?.step.text}&rdquo;{' '}
              {confirmingToggle?.next ? 'done' : 'not done'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmingToggle?.next ? (
                <>
                  This will show as complete on the investor&apos;s dashboard
                  {confirmingToggle.step.impactPoints
                    ? ` and add ${confirmingToggle.step.impactPoints} pts to their wealth score.`
                    : '.'}
                </>
              ) : (
                <>
                  This will show as incomplete on the investor&apos;s dashboard
                  {confirmingToggle?.step.impactPoints
                    ? ` and remove ${confirmingToggle.step.impactPoints} pts from their wealth score.`
                    : '.'}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStaffToggle}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!viewingStep} onOpenChange={(open) => !open && setViewingStep(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{viewingStep?.title ?? 'Prescription step'}</DialogTitle>
            {(viewingStep?.priority || viewingStep?.impactPoints) && (
              <DialogDescription className="flex items-center gap-2 capitalize">
                {viewingStep?.priority && <span>{viewingStep.priority} priority</span>}
                {viewingStep?.priority && viewingStep?.impactPoints ? <span>·</span> : null}
                {viewingStep?.impactPoints ? (
                  <span className="text-primary font-semibold normal-case">
                    +{viewingStep.impactPoints} pts
                  </span>
                ) : null}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{viewingStep?.text}</p>
          </div>
        </DialogContent>
      </Dialog>
      {onEditStep && (
        <EditPrescriptionStepDialog
          step={editingStep}
          onSubmit={onEditStep}
          open={!!editingStep}
          onOpenChange={(open) => !open && setEditingStep(null)}
        />
      )}
    </div>
  )
}

function EditPrescriptionStepDialog({
  step,
  onSubmit: onSubmitProp,
  open,
  onOpenChange,
}: {
  step: PrescriptionStep | null
  onSubmit: (id: string, values: EditCasePrescriptionStepFormValues) => Promise<void>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const form = useForm<
    z.input<typeof editCasePrescriptionStepSchema>,
    unknown,
    EditCasePrescriptionStepFormValues
  >({
    resolver: zodResolver(editCasePrescriptionStepSchema),
    mode: 'onTouched',
    values: step
      ? {
          title: step.title ?? step.text,
          description: step.text,
          priority: step.priority ?? 'medium',
          impactPoints: step.impactPoints ?? 0,
        }
      : { title: '', description: '', priority: 'medium', impactPoints: 5 },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    if (!step) return
    await onSubmitProp(step.id, values)
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Edit step</DialogTitle>
            <DialogDescription>Update this prescription step&apos;s details.</DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="es-title">Title</FieldLabel>
              <Input
                id="es-title"
                placeholder="e.g. Trim concentration risk"
                {...form.register('title')}
              />
              <FieldError errors={[form.formState.errors.title]} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="es-priority">Priority</FieldLabel>
                <Select
                  value={form.watch('priority')}
                  onValueChange={(v) => form.setValue('priority', v as PrescriptionStepPriority)}
                >
                  <SelectTrigger id="es-priority" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="es-impact">Impact points (0-100)</FieldLabel>
                <Input
                  id="es-impact"
                  type="number"
                  min={0}
                  max={100}
                  {...form.register('impactPoints')}
                />
                <FieldError errors={[form.formState.errors.impactPoints]} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="es-description">What should the client do?</FieldLabel>
              {/* `Textarea`'s base styles use `field-sizing-content` (auto-grows to fit its
                  value) with no cap of its own — capped here so a long existing description
                  scrolls inside the textarea itself instead of growing the dialog past the
                  viewport. */}
              <Textarea
                id="es-description"
                rows={4}
                className="max-h-48 resize-none overflow-y-auto"
                placeholder="e.g. Trim your largest holding to under 30% of the portfolio."
                {...form.register('description')}
              />
              <FieldError errors={[form.formState.errors.description]} />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
